import { useContext, useEffect, useRef, useState } from 'react';

import { CMSSection } from '@/cms/CMSSection';
import { BoutiquesList } from '@/components/BoutiquesList';
import { Container } from '@/components/Container';
import { FindAStore } from '@/components/FindAStore';
import type { ContentfulPlaceholderStoresFragment } from '@/graphql/contentful/api';
import type { StoreHit } from '@/integrations/algolia/types';
import { isAbortLikeError } from '@/lib/errors';
import { generateDataTestId } from '@/lib/tests';
import { DraftModeContext } from '@/providers/DraftModeProvider/DraftModeContext';
import { useRegionProvider } from '@/providers/RegionProvider';

import { makeQueryString } from './helpers';

interface StoresResultsProps {
  info: ContentfulPlaceholderStoresFragment;
  stores: StoreHit[];
}

export const Stores = (props: ContentfulPlaceholderStoresFragment) => {
  const {
    country: { code },
  } = useRegionProvider();
  const [stores, setStores] = useState<StoreHit[]>([]);
  const draftMode = useContext(DraftModeContext);
  const isActiveRef = useRef(true);

  useEffect(() => {
    const controller = new AbortController();
    const fetchStores = async () => {
      isActiveRef.current = true;

      const query = makeQueryString({
        countryCode: code,
        mode: props.type,
        numberOfStores: `${props.numberOfStores}`,
        boutiqueReferenceKeys: props.boutiqueReferenceKeys,
        webVisible: true,
        draftMode: draftMode,
      });
      try {
        const result = await fetch(`/api/boutiques/?${query}`, {
          signal: controller.signal,
        });
        const stores = await result.json();
        if (!isActiveRef.current) return;
        setStores(stores);
      } catch (err) {
        if (isAbortLikeError(err)) {
          return;
        }
        console.warn('[Stores] fetch boutiques failed:', err);
      }
    };
    fetchStores();
    return () => {
      controller.abort();
      isActiveRef.current = false;
    };
  }, [
    code,
    draftMode,
    props.boutiqueReferenceKeys,
    props.numberOfStores,
    props.type,
  ]);

  const [isInsightsReady, setIsInsightsReady] = useState<boolean>(false);

  useEffect(() => {
    setIsInsightsReady(true);
  }, []);

  if (!isInsightsReady) {
    return null;
  }

  return (
    <CMSSection
      {...generateDataTestId('stores', 'section')}
      data-test-id='StoresSection'
    >
      <Container>
        <StoresResults info={props} stores={stores}></StoresResults>
      </Container>
    </CMSSection>
  );
};

export const StoresResults = ({ info, stores }: StoresResultsProps) => {
  if (!stores.length) {
    return (
      <FindAStore
        cardType='noResult'
        data-test-id='StoresResults-noResultCard'
        info={info}
        noStoreResults={true}
      />
    );
  }

  return (
    <BoutiquesList
      data-test-id='StoresResults-BoutiquesList'
      info={info}
      stores={stores}
    ></BoutiquesList>
  );
};
